import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/themeContext';
import { IconLogout, IconSun, IconMoon } from '../icons';
import ServerClock from './ServerClock';

const LINKS = [
  { to: '/', label: 'Servers', end: true },
  { to: '/apps', label: 'Apps' },
];

const linkClass = ({ isActive }) =>
  `px-2.5 py-2 rounded text-base font-medium leading-5 transition-colors ${
    isActive ? 'bg-navActive text-white' : 'text-white hover:bg-navActive/60'
  }`;

const Navigation = () => {
  const { currentUser, logout } = useAuth();
  const { resolved, toggle } = useTheme();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <nav
      className="bg-nav text-white relative"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      <div className="px-4 sm:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-[18px] min-w-0">
          <div className="flex items-center gap-1.5 shrink-0">
            <img src="/Icon.svg" alt="" className="h-9 w-9 shrink-0" />
            <span className="text-xl font-bold leading-6 tracking-[-0.44px] whitespace-nowrap">
              PNM Alert
            </span>
          </div>
          <div className="hidden md:flex items-center gap-5">
            {LINKS.map((link) => (
              <NavLink key={link.to} to={link.to} end={link.end} className={linkClass}>
                {link.label}
              </NavLink>
            ))}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          <ServerClock />
          <button
            type="button"
            onClick={toggle}
            aria-label={resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            title={resolved === 'dark' ? 'Light theme' : 'Dark theme'}
            className="inline-flex items-center justify-center p-2 -m-2 rounded text-white hover:opacity-80 transition-opacity"
          >
            {resolved === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
          </button>
          {currentUser?.username && (
            <span className="text-sm text-white/80">{currentUser.username}</span>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 py-2 rounded text-base font-medium text-white hover:opacity-80 transition-opacity"
          >
            <IconLogout size={16} />
            Logout
          </button>
        </div>

        {/* Mobile menu button */}
        <div className="md:hidden">
          <button
            onClick={() => setIsMobileMenuOpen((o) => !o)}
            aria-label="Toggle navigation"
            className="inline-flex items-center justify-center p-2 min-h-11 min-w-11 rounded-md text-white hover:bg-navActive/60"
          >
            {isMobileMenuOpen ? (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden absolute left-0 right-0 bg-nav px-2 pt-2 pb-3 space-y-1 z-40">
          {LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              onClick={() => setIsMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center px-3 py-2 min-h-11 rounded-md text-base font-medium ${
                  isActive ? 'bg-navActive text-white' : 'text-white hover:bg-navActive/60'
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <div className="border-t border-white/20 pt-2 mt-2">
            {currentUser?.username && (
              <div className="px-3 py-1 text-sm text-white/80">{currentUser.username}</div>
            )}
            <ServerClock className="px-3 py-2" />
            <button
              type="button"
              onClick={toggle}
              className="w-full px-3 py-2 min-h-11 rounded-md text-base font-medium text-white hover:bg-navActive/60 inline-flex items-center gap-1.5 transition-colors"
            >
              {resolved === 'dark' ? <IconSun size={16} /> : <IconMoon size={16} />}
              {resolved === 'dark' ? 'Light theme' : 'Dark theme'}
            </button>
            <button
              onClick={() => { setIsMobileMenuOpen(false); handleLogout(); }}
              className="w-full text-left flex items-center gap-1.5 px-3 py-2 min-h-11 rounded-md text-base font-medium text-white hover:bg-navActive/60"
            >
              <IconLogout size={16} />
              Logout
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
